import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { SelectPagination } from "@foris/avocado-suite";
import { useReviewerOptionsLoader } from "@services/collections/assignment-proposal/assignmentProposalServices";
import type { ReviewerOption } from "@services/types/assignment-proposal/assignmentProposalTypes";
import styles from "./reviewerSelect.module.scss";

interface ReviewerSelectProps {
  value: ReviewerOption | null;
  disabled?: boolean;
  onChange: (reviewer: ReviewerOption | null) => void;
}

const FIRST_PAGE = { page: 1 };

/**
 * Searchable, paginated, single-selection list of the users who can give the intermediate
 * approval. Pages go in the library's own "additional" value, so a new search term starts again at
 * page 1. A failed load says so: an empty list alone would read as "nobody can approve".
 */
const ReviewerSelect = ({ value, disabled = false, onChange }: ReviewerSelectProps) => {
  const { t } = useTranslation();
  const loadReviewerOptions = useReviewerOptionsLoader();
  const [hasLoadError, setHasLoadError] = useState(false);

  const loadOptions = useCallback(
    async (searchTerm: string, _loadedOptions: unknown, additional?: { page?: number }) => {
      const page = additional?.page ?? FIRST_PAGE.page;

      try {
        const { options, hasMore } = await loadReviewerOptions(searchTerm, page);
        setHasLoadError(false);

        return { options, hasMore, additional: { page: page + 1 } };
      } catch (error) {
        console.error("[ReviewerOptions]", error);
        setHasLoadError(true);

        return { options: [], hasMore: false };
      }
    },
    [loadReviewerOptions],
  );

  return (
    <SelectPagination
      className={styles.reviewerSelect}
      label={t("assignation-edit.proposal.send-modal.reviewer.label")}
      placeholder={t("assignation-edit.proposal.send-modal.reviewer.placeholder")}
      error={hasLoadError ? t("assignation-edit.proposal.send-modal.reviewer.error") : undefined}
      value={value}
      loadOptions={loadOptions}
      additional={FIRST_PAGE}
      debounceTimeout={500}
      disabled={disabled}
      onChange={(option: ReviewerOption | null) => onChange(option ?? null)}
    />
  );
};

export default ReviewerSelect;
